import { PencilIcon, PlusIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import { useSWRConfig } from 'swr';

import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { SimpleComboboxField } from '../../../components/combobox/SimpleComboboxField';
import { useVatRates, VAT_RATES_SWR_KEY } from '../models';
import { CreateVatRateDialog } from './CreateVatRateDialog';
import { UpdateVatRateDialog } from './UpdateVatRateDialog';
import { decimateAndFormatNumber } from '../../../utils/number';

export interface IMinimalVatRate {
  id: string;
  amount: number;
}

export interface IVatRateComboboxProps {
  name: string;
  labelText?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: () => void;
  isNullable?: boolean;
}

export const vatRateToComboboxItem = (val: IMinimalVatRate) => {
  return {
    key: val.id,
    name: `${val.id} - ${decimateAndFormatNumber(val.amount, 2, {
      decimalSeperator: ',',
    })}%`,
  };
};

export const VatRateComboboxField: React.FC<IVatRateComboboxProps> = (props) => {
  const { labelText, name, ...otherProps } = props;
  const { data } = useVatRates();
  const { mutate } = useSWRConfig();
  const [field, _meta, helpers] = useField({ name });

  const vatRates = data ?? [];

  const items: ISimpleComboboxItem[] = useMemo(() => {
    return data ? data.map(vatRateToComboboxItem) : [];
  }, [vatRates]);

  const refetch = useCallback(() => {
    mutate(VAT_RATES_SWR_KEY);
  }, [mutate]);

  const value: ISimpleComboboxItem | null = field.value;
  return (
    <div>
      <div className="flex items-center">
        <SimpleComboboxField labelText={labelText ?? 'Aard'} items={items} name={name} {...otherProps} />
        <div className="flex-end flex justify-end gap-2 ml-2">
          {value && (
            <UpdateVatRateDialog
              id={value.key}
              onSubmit={(data) => {
                helpers.setValue(vatRateToComboboxItem(data));
                refetch();
              }}
              triggerText={<PencilIcon className="button-icon" />}
            />
          )}

          <CreateVatRateDialog
            onSubmit={(data) => {
              helpers.setValue(vatRateToComboboxItem(data));
              refetch();
            }}
            triggerText={<PlusIcon className="button-icon" />}
          />
        </div>
      </div>
    </div>
  );
};
